import React, { useContext, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { ThemeProvider } from 'react-jss';
import { ReactNotifications } from 'react-notifications-component';
import { useCookie, useEffectOnce, useFullscreen } from 'react-use';
import { Route, Routes } from 'react-router-dom';
import { ToastProvider } from 'react-toast-notifications';
import jwtDecode from 'jwt-decode';
import { TourProvider } from '@reactour/tour';
import Swal from 'sweetalert2';
import ThemeContext from '../contexts/themeContext';

import Wrapper from '../layout/Wrapper/Wrapper';
import Portal from '../layout/Portal/Portal';
import { Toast, ToastContainer } from '../components/bootstrap/Toasts';
import Modal, { ModalBody } from '../components/bootstrap/Modal';
import useDarkMode from '../hooks/useDarkMode';
import COLORS from '../common/data/enumColors';
import { getOS, cookieNameGenerator } from '../helpers/helpers';
import steps, { styles } from '../steps';
import Aside from '../layout/Aside/Aside';
import AuthModule from '../modules/AuthModule';
import OidcModule from '../modules/OidcModule';
import { detectEmbedMode } from '../helpers/embedMode';
import { notifyEmbedBusy, notifyEmbedReady } from '../helpers/embedBridge';
import { ambilTujuanEmbed } from '../helpers/embedTujuan';

const accessToken = localStorage.getItem('accessToken') || null;
const appToken = localStorage.getItem('appToken') || null;
const IS_SSO = process.env.REACT_APP_SSO == 'true' || false;
const SSO_KEY = process.env.REACT_APP_SSO_KEY || null;
const SSO_HOMEPAGE = process.env.REACT_APP_SSO_HOMEPAGE || null;

/**
 * Dibuka dari kartu inbox aplikasi mobile OneLNK (WebView), dibaca sekali dari
 * URL pertama. WebView memblokir host SSO tempat OP dan beranda SSO berada:
 * `OidcModule.bisaDiJendelaIni()` hanya mengenali iframe, jadi embed perlu
 * dicek sendiri supaya login tetap lewat cookie (jalur lama).
 */
const EMBED_AWAL = detectEmbedMode();

/** Ke beranda SSO; di embed cukup beri tahu dan lepas penutup loading aplikasi. */
const keBerandaSso = () => {
	if (EMBED_AWAL) {
		Swal.fire({
			heightAuto: false,
			title: 'Sesi tidak ditemukan',
			text: 'Silakan login ke OneLNK lalu buka kartu ini lagi.',
			icon: 'info',
		});
		notifyEmbedReady({ isEmbed: true });
		return null;
	}
	AuthModule.goONELNKHomapage();
	window.location.href = SSO_HOMEPAGE;
	return SSO_HOMEPAGE;
};

/**
 * Sesudah login dari cookie: kembali ke halaman kartu bila tercatat (embed,
 * lihat helpers/embedTujuan), selain itu muat ulang seperti sebelumnya.
 */
const muatSetelahLogin = () => {
	const tujuan = ambilTujuanEmbed();
	if (tujuan) {
		window.location.replace(tujuan);
		return;
	}
	window.location.reload();
};

const App = () => {
	const [cookie] = useCookie([cookieNameGenerator('ssocookies')]);
	const [cookieAcc] = useCookie([cookieNameGenerator('ssoaccount')]);
	const [cookiePhone] = useCookie([cookieNameGenerator('ssophonenumber')]);
	getOS();
	const [loadingSubmit, setLoadingSubmit] = useState(false);

	/**
	 * Dark Mode
	 */
	const { themeStatus, darkModeStatus } = useDarkMode();
	const theme = {
		theme: themeStatus,
		primary: COLORS.PRIMARY.code,
		secondary: COLORS.SECONDARY.code,
		success: COLORS.SUCCESS.code,
		info: COLORS.INFO.code,
		warning: COLORS.WARNING.code,
		danger: COLORS.DANGER.code,
		dark: COLORS.DARK.code,
		light: COLORS.LIGHT.code,
	};
	const testJSON = (text) => {
		if (typeof text !== 'string') {
			return false;
		}
		try {
			JSON.parse(text);
			return true;
		} catch (error) {
			return false;
		}
	};
	useEffectOnce(() => {
		const isSSOLogin = async () => {
			try {
				// Kegagalan login OIDC dipulangkan callback be-checklist lewat
				// `?oidc_error=`. Dibaca SEBELUM blok appToken di bawah: URL-nya
				// dibersihkan secara sinkron, sedangkan `.then` milik
				// `generateToken` baru berjalan sesudahnya -- saat itu URL sudah
				// bersih, dan pagar yang membaca URL di sana akan buta.
				const galatOidc = OidcModule.aktif() ? OidcModule.ambilGalatDariUrl() : null;

				if (!appToken) {
					AuthModule.generateToken().then(() => {
						// 🔴 JANGAN memuat ulang selagi dialog galat OIDC tampil:
						// reload membuangnya sebelum sempat dibaca. appToken toh
						// sudah tersimpan oleh `generateToken`.
						//
						// Tiket SENGAJA tidak ikut dipagari: penebusan tiket di
						// bawah digerbangi `appToken` yang dibaca SEKALI saat modul
						// dimuat, jadi reload inilah yang membuat penebusannya bisa
						// berjalan.
						if (galatOidc) return null;
						return window.location.reload();
					});
				}

				// Ditangani SEBELUM tiket dan bootstrap SSO, dan alurnya DIHENTIKAN
				// di sini. Cabang di bawah melihat "cookie SSO ada, accessToken
				// belum" lalu melempar pengguna ke OP lagi, dan OP memulangkannya
				// dengan galat yang sama -- lingkaran tanpa akhir.
				//
				// `oidc_gagal` ikut dipasang: memuat ulang halaman (URL-nya sudah
				// bersih) tidak lagi mengalihkan ke OP, tapi jatuh ke jalur login
				// lama selama tab ini terbuka.
				if (galatOidc) {
					OidcModule.bersihkanGalatDariUrl();
					sessionStorage.setItem('oidc_gagal', '1');
					Swal.fire({
						heightAuto: false,
						icon: 'error',
						title: 'Login Gagal',
						text: galatOidc,
					});
					return null;
				}

				// Penebusan tiket OIDC HARUS mendahului bootstrap SSO di bawah.
				// Kalau tidak, cabang "tidak ada kuki" di bawah membuang
				// localStorage lalu pindah ke beranda SSO, dan tiket ikut hangus --
				// gejalanya login yang gagal senyap, bukan galat.
				//
				// Digerbangi `appToken` karena `authHeader()` menghilangkan
				// `x-application-token` saat appToken belum ada, sehingga
				// penebusan dijawab 401. Itu tidak membakar tiket, dan blok di atas
				// memuat ulang halaman begitu token aplikasinya terbit -- tiketnya
				// masih di URL.
				const tiketOidc = OidcModule.aktif() ? OidcModule.ambilTiketDariUrl() : null;
				if (tiketOidc && appToken) {
					setLoadingSubmit(true);
					try {
						const sesi = await OidcModule.tebusTiket(tiketOidc);
						const dipakai = await OidcModule.terapkanSesi(sesi);
						OidcModule.bersihkanTiketDariUrl();
						if (dipakai) {
							sessionStorage.removeItem('oidc_gagal');
							return window.location.reload();
						}
						// Sesi tidak layak (tanpa accessToken / tanpa current_person).
						sessionStorage.setItem('oidc_gagal', '1');
					} catch (errTiket) {
						OidcModule.bersihkanTiketDariUrl();
						// Penanda ini mencegah lingkaran: tanpa itu jalur di bawah
						// akan mengalihkan ke OIDC lagi, gagal lagi, selamanya.
						sessionStorage.setItem('oidc_gagal', '1');
					} finally {
						setLoadingSubmit(false);
					}
				}

				const isEmailSSO = cookieAcc ? window.atob(cookieAcc) : null;
				const isHPSSO = cookiePhone ? window.atob(cookiePhone) : null;
				if (IS_SSO) {
					if (!cookie) {
						return keBerandaSso();
					}
					const decryptCookieSSO = window.atob(cookie);
					if (!decryptCookieSSO) {
						return keBerandaSso();
					}
					const tokenAccSSO = testJSON(decryptCookieSSO)
						? JSON.parse(decryptCookieSSO)
						: null;
					if (!tokenAccSSO?.sso_key) {
						return keBerandaSso();
					}
					if (tokenAccSSO?.sso_key && tokenAccSSO?.sso_key !== SSO_KEY) {
						return keBerandaSso();
					}

					const decodeToken = tokenAccSSO ? jwtDecode(tokenAccSSO?.accessToken) : null;
					//  isEmailSSo then get accessToken
					if (isEmailSSO && decodeToken && !accessToken) {
						setLoadingSubmit(true);
						notifyEmbedBusy({ isEmbed: EMBED_AWAL });
						const parseEmail = JSON.parse(isEmailSSO);
						const newPayload = {
							name: parseEmail?.name,
							email: parseEmail?.idTokenClaims?.preferred_username,
							account_id: parseEmail?.localAccountId,
							username: parseEmail?.localAccountId,
						};
						return AuthModule.loginSSO(newPayload)
							.then(async (res) => {
								if (res.accessToken) {
									const verifyToken = jwtDecode(res.accessToken);
									localStorage.setItem('accessToken', JSON.stringify(res));
									localStorage.setItem('list_roles', JSON.stringify(res?.role));

									const default_role = res.role.find((ro) => ro.is_default) || {
										role_name: 'guest user',
									};
									if (verifyToken.details.hris_org_tree.current_person) {
										localStorage.setItem(
											'roles',
											JSON.stringify(default_role.role_name),
										);
										localStorage.setItem(
											'selected_role',
											JSON.stringify(
												`${default_role.org_name_short} ${default_role.position_name}`,
											),
										);
										localStorage.setItem(
											'position',
											JSON.stringify({
												...default_role,
											}),
										);

										// get menu
										await AuthModule.getMenu(default_role.role_name).then(
											() => {
												localStorage.setItem(
													'photo',
													JSON.stringify({ ...res?.photo }),
												);
												muatSetelahLogin();
											},
										);
									}
								}
							})
							.finally(() => {
								setLoadingSubmit(false);
							});
					}

					// login by phone
					if (
						!isEmailSSO &&
						isHPSSO &&
						decodeToken &&
						!accessToken &&
						tokenAccSSO?.sso_key
					) {
						setLoadingSubmit(true);
						notifyEmbedBusy({ isEmbed: EMBED_AWAL });

						const parseEmail = JSON.parse(isHPSSO);
						return AuthModule.loginPhone(parseEmail)
							.then(async (res) => {
								if (res.accessToken) {
									const verifyToken = jwtDecode(res.accessToken);
									localStorage.setItem('accessToken', JSON.stringify(res));
									localStorage.setItem('list_roles', JSON.stringify(res?.role));

									const default_role = res.role.find((ro) => ro.is_default) || {
										role_name: 'guest user',
									};
									if (verifyToken.details.hris_org_tree.current_person) {
										localStorage.setItem(
											'selected_role',
											JSON.stringify(
												`${default_role.org_name_short} ${default_role.position_name}`,
											),
										);
										localStorage.setItem(
											'roles',
											JSON.stringify(default_role.role_name),
										);
										localStorage.setItem(
											'position',
											JSON.stringify({
												...default_role,
											}),
										);

										// get menu
										await AuthModule.getMenu(default_role.role_name).then(
											() => {
												localStorage.setItem(
													'photo',
													JSON.stringify({ ...res?.photo }),
												);
												muatSetelahLogin();
											},
										);
									}
								}
							})
							.catch((err) => {
								Swal.fire({
									heightAuto: false,
									title: 'Warning!',
									text: err,
									icon: 'info',
								});
								notifyEmbedReady({ isEmbed: EMBED_AWAL });
							})
							.finally(() => {
								setLoadingSubmit(false);
							});
					}
					//  !isEmailSSo && !isHPSSO then get accessToken
					if (
						!isEmailSSO &&
						!isHPSSO &&
						decodeToken &&
						!accessToken &&
						tokenAccSSO?.sso_key
					) {
						// Pengalihan OIDC MENGGANTIKAN langkah di bawah ini, yaitu
						// login ulang memakai password yang dibawa cookie SSO.
						// Letaknya sengaja BERSARANG di sini: di titik ini cookie SSO
						// yang sah (sso_key cocok) sudah terbukti ada, jadi akun guest,
						// aplikasi mobile, dan pengunjung anonim tidak pernah
						// menyentuhnya.
						if (
							OidcModule.aktif() &&
							OidcModule.bisaDiJendelaIni() &&
							!EMBED_AWAL &&
							!sessionStorage.getItem('oidc_gagal')
						) {
							window.location.href = OidcModule.urlLoginUntukHalamanIni();
							return null;
						}
						setLoadingSubmit(true);
						notifyEmbedBusy({ isEmbed: EMBED_AWAL });
						const accTokenParse = jwtDecode(tokenAccSSO.accessToken);
						return AuthModule.login(
							accTokenParse.username.toUpperCase(),
							accTokenParse.password,
						)
							.then(async (res) => {
								if (res.accessToken) {
									const verifyToken = jwtDecode(res.accessToken);
									localStorage.setItem('accessToken', JSON.stringify(res));
									localStorage.setItem('list_roles', JSON.stringify(res?.role));

									const default_role = res.role.find((ro) => ro.is_default) || {
										role_name: 'guest user',
									};
									if (verifyToken.details.hris_org_tree.current_person) {
										localStorage.setItem(
											'selected_role',
											JSON.stringify(
												`${default_role.org_name_short} ${default_role.position_name}`,
											),
										);
										localStorage.setItem(
											'roles',
											JSON.stringify(default_role.role_name),
										);
										localStorage.setItem(
											'position',
											JSON.stringify({
												...default_role,
											}),
										);

										// get menu
										await AuthModule.getMenu(default_role.role_name).then(
											() => {
												localStorage.setItem(
													'photo',
													JSON.stringify({ ...res?.photo }),
												);
												muatSetelahLogin();
											},
										);
									}
								}
							})
							.finally(() => {
								setLoadingSubmit(false);
							});
					}
				}
				return isEmailSSO;
			} catch (err) {
				notifyEmbedReady({ isEmbed: EMBED_AWAL });
				return Swal.fire({
					heightAuto: false,
					title: 'Warning!',
					text: err,
					icon: 'info',
				});
			}
		};
		isSSOLogin();
	});

	useEffect(() => {
		if (darkModeStatus) {
			document.documentElement.setAttribute('theme', 'dark');
		}
		return () => {
			document.documentElement.removeAttribute('theme');
		};
	}, [darkModeStatus]);

	/**
	 * Full Screen
	 */
	const { fullScreenStatus, setFullScreenStatus } = useContext(ThemeContext);
	const ref = useRef(null);
	useFullscreen(ref, fullScreenStatus, {
		onClose: () => setFullScreenStatus(false),
	});

	/**
	 * Modern Design
	 */
	useLayoutEffect(() => {
		if (process.env.REACT_APP_MODERN_DESGIN === 'true') {
			document.body.classList.add('modern-design');
		} else {
			document.body.classList.remove('modern-design');
		}
	});

	//	Add paths to the array that you don't want to be "Aside".
	const withOutAsidePages = ['login'];

	return (
		<ThemeProvider theme={theme}>
			<ToastProvider components={{ ToastContainer, Toast }}>
				<TourProvider
					steps={steps}
					styles={styles}
					showNavigation={false}
					showBadge={false}>
					<Modal
						isOpen={loadingSubmit}
						size='sm'
						isCentered
						setIsOpen={() => {}}
						isStaticBackdrop>
						<ModalBody
							style={{ backgroundColor: '#6c5dd3', color: 'white' }}
							className='text-center'>
							<button className='btn btn-primary' type='button' disabled>
								<span
									className='spinner-grow spinner-grow-sm'
									role='status'
									aria-hidden='true'
								/>
								&nbsp;
								<span
									className='spinner-grow spinner-grow-sm'
									role='status'
									aria-hidden='true'
								/>
								&nbsp;
								<span
									className='spinner-grow spinner-grow-sm'
									role='status'
									aria-hidden='true'
								/>
								&nbsp;
								<span className='sr-only'>Loading...</span>
							</button>
						</ModalBody>
					</Modal>
					<div
						ref={ref}
						className='app'
						style={{
							backgroundColor: fullScreenStatus && 'var(--bs-body-bg)',
							zIndex: fullScreenStatus && 1,
							overflow: fullScreenStatus && 'scroll',
						}}>
						<Routes>
							{withOutAsidePages.map((path) => (
								<Route key={path} path={path} />
							))}
							<Route path='*' element={accessToken ? <Aside /> : null} />
						</Routes>
						<Wrapper />
					</div>
					<Portal id='portal-notification'>
						<ReactNotifications />
					</Portal>
				</TourProvider>
			</ToastProvider>
		</ThemeProvider>
	);
};

export default App;
